"use client";

import { Download, Loader2, MoreVertical, Volume2, VolumeX } from "lucide-react";
import { useTranslations } from "next-intl";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Icon } from "@/components/ui/icon";
import {
  ArrowLeftIcon,
  type ArrowLeftIconHandle,
  XIcon,
  type XIconHandle,
} from "@/components/widget/icons/animated";
import { useIconHover } from "@/lib/use-icon-hover";
import { cn } from "@/lib/utils";

interface ConversationHeaderProps {
  title: string;
  subtitle?: string;
  organizationLogoUrl?: string | null;
  muted: boolean;
  onToggleMute: () => void;
  /** Omit when there's nowhere to go back to (Messages is the only
   *  enabled tab) — the back button hides entirely so the visitor
   *  doesn't see a dead control. */
  onBack?: () => void;
  onClose: () => void;
  /** Show the "Resolve conversation" action — true only while an operator is
   *  connected (visitor can end the handoff and return to the AI assistant). */
  showResolve?: boolean;
  onResolve?: () => void;
  /** Download the full conversation as a .txt transcript. Omitted (button
   *  hidden) when there's no conversation to export (e.g. an empty/new thread). */
  onDownloadTranscript?: () => void;
  /** True while the transcript is being fetched — disables the button + shows a
   *  spinner so a slow export can't be double-triggered. */
  downloadingTranscript?: boolean;
  /** Expand toggle — present only on routes where the widget can
   *  widen (messages, single article). Omitting it hides the chip. */
  expanded?: boolean;
  onToggleExpand?: () => void;
}

/**
 * Sticky conversation header for the Messages tab. Back arrow + Wexio
 * mark + sender name & subtitle + close button. Replaces the generic
 * widget chrome while the visitor is in a conversation.
 *
 * Both animated icons (`ArrowLeftIcon`, `XIcon`) animate on the
 * parent button's hover — `useIconHover` propagates the hover signal
 * via ref handles so visitors see motion when they hover a tap
 * target, not when they happen to land on the SVG itself.
 */
export function ConversationHeader({
  title,
  subtitle,
  organizationLogoUrl,
  muted,
  onToggleMute,
  onBack,
  onClose,
  showResolve,
  onResolve,
  onDownloadTranscript,
  downloadingTranscript,
  expanded,
  onToggleExpand,
}: ConversationHeaderProps) {
  const t = useTranslations("header");
  const tHeader = useTranslations("header");
  const back = useIconHover<ArrowLeftIconHandle>();
  const close = useIconHover<XIconHandle>();

  return (
    <header
      data-wx-top-bar
      className={cn("flex items-center gap-3 border-b border-wx-border bg-wx-bg px-3 py-3")}
    >
      {onBack && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label={t("back")}
          onClick={onBack}
          className="size-8 shrink-0"
          {...back.parentHandlers}
        >
          <ArrowLeftIcon ref={back.iconRef} size={16} />
        </Button>
      )}
      {organizationLogoUrl && (
        <span className="h-9 w-9 shrink-0 overflow-hidden rounded-full">
          {/* biome-ignore lint/performance/noImgElement: uploaded image of arbitrary host/dimensions; next/image needs known sizes + an allowlist */}
          <img src={organizationLogoUrl} alt={title} className="h-full w-full object-cover" />
        </span>
      )}
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold text-wx-fg">{title}</p>
        {subtitle && <p className="truncate text-xs text-wx-fg-muted">{subtitle}</p>}
      </div>
      {/* Secondary actions collapse into a single overflow (⋮) menu so the
          header stays compact — only Expand + Close remain inline. */}
      <HeaderOverflowMenu
        muted={muted}
        onToggleMute={onToggleMute}
        showResolve={!!showResolve && !!onResolve}
        onResolve={onResolve}
        onDownloadTranscript={onDownloadTranscript}
        downloadingTranscript={!!downloadingTranscript}
      />
      {onToggleExpand && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label={expanded ? tHeader("collapse") : tHeader("expand")}
          onClick={onToggleExpand}
          className="group size-8 shrink-0"
        >
          <span className="block transition-transform duration-200 ease-out group-hover:scale-110 group-active:scale-90">
            <ExpandGlyph expanded={!!expanded} />
          </span>
        </Button>
      )}
      <Button
        type="button"
        variant="ghost"
        size="icon"
        aria-label={t("close")}
        onClick={onClose}
        className="size-8 shrink-0"
        {...close.parentHandlers}
      >
        <XIcon ref={close.iconRef} size={16} />
      </Button>
    </header>
  );
}

/**
 * Inline CircleCheck — the "resolve conversation" action glyph. Inline
 * (like `ExpandGlyph`) to keep the header's import list tight.
 */
function ResolveGlyph() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M21.801 10A10 10 0 1 1 17 3.335" />
      <path d="m9 11 3 3L22 4" />
    </svg>
  );
}

/**
 * Overflow (⋮) menu that collapses the header's secondary actions — download
 * transcript, sound on/off, and (when an operator is connected) resolve — into
 * a single trigger so the header stays compact. Self-contained: owns its open
 * state and closes on outside-click, Escape, or after an item is chosen. No
 * shared dropdown primitive exists in the widget, so this mirrors the anchored
 * pattern the composer pickers use.
 */
function HeaderOverflowMenu({
  muted,
  onToggleMute,
  showResolve,
  onResolve,
  onDownloadTranscript,
  downloadingTranscript,
}: {
  muted: boolean;
  onToggleMute: () => void;
  showResolve: boolean;
  onResolve?: () => void;
  onDownloadTranscript?: () => void;
  downloadingTranscript: boolean;
}) {
  const tHeader = useTranslations("header");
  const tSound = useTranslations("sound");
  const tMessages = useTranslations("messages");
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointer = (e: MouseEvent) => {
      // composedPath (not e.target): in the Shadow-DOM embed a document listener
      // sees events retargeted to the shadow host, so contains() misreads
      // in-menu clicks as outside. composedPath pierces the shadow boundary and
      // is the ordinary path in light DOM.
      if (ref.current && !e.composedPath().includes(ref.current)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const run = (fn?: () => void) => () => {
    setOpen(false);
    fn?.();
  };

  return (
    <div ref={ref} className="relative shrink-0">
      <Button
        type="button"
        variant="ghost"
        size="icon"
        aria-label={tHeader("moreActions")}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="group size-8"
      >
        <span className="block transition-transform duration-200 ease-out group-hover:scale-110 group-active:scale-90">
          <Icon icon={MoreVertical} size={18} />
        </span>
      </Button>
      {open && (
        <div
          role="menu"
          className="absolute top-full right-0 z-30 mt-1 min-w-44 overflow-hidden rounded-wx-sm border border-wx-border bg-wx-bg py-1 shadow-lg"
        >
          {onDownloadTranscript && (
            <MenuItem
              icon={<Icon icon={downloadingTranscript ? Loader2 : Download} size={16} />}
              label={tHeader("downloadTranscript")}
              onClick={run(onDownloadTranscript)}
              disabled={downloadingTranscript}
              spinning={downloadingTranscript}
            />
          )}
          <MenuItem
            icon={<Icon icon={muted ? VolumeX : Volume2} size={16} />}
            label={muted ? tSound("unmute") : tSound("mute")}
            onClick={run(onToggleMute)}
          />
          {showResolve && (
            <MenuItem
              icon={<ResolveGlyph />}
              label={tMessages("resolveConversation")}
              onClick={run(onResolve)}
            />
          )}
        </div>
      )}
    </div>
  );
}

function MenuItem({
  icon,
  label,
  onClick,
  disabled,
  spinning,
}: {
  icon: ReactNode;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  spinning?: boolean;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      disabled={disabled}
      className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm text-wx-fg transition-colors hover:bg-wx-bg-elevated disabled:cursor-default disabled:opacity-60"
    >
      <span className={cn("shrink-0 text-wx-fg-muted", spinning && "animate-spin")}>{icon}</span>
      <span className="truncate">{label}</span>
    </button>
  );
}

/**
 * Inline Maximize2 / Minimize2 — keeps `ConversationHeader`'s import
 * list tight without pulling another file. Lucide-react ships these
 * as plain SVG components.
 */
function ExpandGlyph({ expanded }: { expanded: boolean }) {
  if (expanded) {
    return (
      // Minimize2
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <polyline points="4 14 10 14 10 20" />
        <polyline points="20 10 14 10 14 4" />
        <line x1="14" y1="10" x2="21" y2="3" />
        <line x1="3" y1="21" x2="10" y2="14" />
      </svg>
    );
  }
  return (
    // Maximize2
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="15 3 21 3 21 9" />
      <polyline points="9 21 3 21 3 15" />
      <line x1="21" y1="3" x2="14" y2="10" />
      <line x1="3" y1="21" x2="10" y2="14" />
    </svg>
  );
}
